// src/i18n/LanguageContext.js
//
// One English / Spanish switch for the whole app.
//
// HOW IT WORKS
// - <LanguageProvider> (wrapped around <App/> in src/index.js) holds the
//   current language: "en" or "es".
// - Any component calls `const { t, lang } = useLanguage();` and wraps its
//   fixed English text:  t("Clock In")  ->  "Entrada" in Spanish mode.
//   Placeholders:        t("Selected: {name}", { name: file.name })
// - The English text IS the key. If a phrase has no Spanish entry yet, the
//   English shows, so nothing ever renders blank.
// - The choice is remembered on this device (localStorage) AND on the
//   person's users/{uid} doc (field `language`), so it follows them from
//   phone to laptop.
// - Plain functions that are not components (alert text in a util, etc.)
//   use `tNow("...")`, which reads the current language without a hook.
//
// Free text people type (notes, job descriptions) is NOT in the dictionary;
// that goes through <TranslatedText>, see TranslatedText.js.
//
// Pages that never call useLanguage() are untouched and stay English.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import ES from "./es";

const STORAGE_KEY = "servicing_lang";
const SUPPORTED = ["en", "es"];

/* -------------------------------------------------------------------------- */
/* Module-level current language, for tNow() outside React                   */
/* -------------------------------------------------------------------------- */

const readStoredLang = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED.includes(v) ? v : "en";
  } catch (e) {
    return "en";
  }
};

// "View as" (utils/viewAs.js) stores the person being viewed in this tab.
// Read it here directly (not imported) to avoid a circular import.
const readViewAs = () => {
  try {
    const raw = sessionStorage.getItem("servicing_view_as");
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};
const viewingAs = readViewAs();

// While viewing as someone, start in THEIR language and never save changes.
let currentLang = viewingAs ? (viewingAs.language === "es" ? "es" : "en") : readStoredLang();

export const getCurrentLanguage = () => currentLang;

const fill = (text, vars) => {
  if (!vars) return text;
  return String(text).replace(/\{(\w+)\}/g, (m, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) && vars[name] !== undefined && vars[name] !== null
      ? String(vars[name])
      : m
  );
};

/** Translate fixed UI text for a given language. English text is the key. */
export const translateUi = (lang, text, vars) => {
  if (text === null || text === undefined) return "";
  const key = String(text);
  if (lang === "es") {
    const hit = ES[key];
    if (typeof hit === "string" && hit.length) return fill(hit, vars);
  }
  // "Open@@slot" -> English "Open". The @@ part only picks a different
  // Spanish word when one English word means two things on different screens.
  const at = key.indexOf("@@");
  return fill(at >= 0 ? key.slice(0, at) : key, vars);
};

/** Same as t(), for code that is not a component (alerts in utils, etc). */
export const tNow = (text, vars) => translateUi(currentLang, text, vars);

/* -------------------------------------------------------------------------- */
/* Context                                                                    */
/* -------------------------------------------------------------------------- */

const defaultValue = {
  lang: "en",
  isSpanish: false,
  setLang: () => {},
  toggleLang: () => {},
  t: (text, vars) => translateUi("en", text, vars),
};

const LanguageContext = createContext(defaultValue);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(currentLang);
  const [uid, setUid] = useState(null);

  // Keep the module copy, <html lang> and this device in step.
  useEffect(() => {
    currentLang = lang;
    try {
      document.documentElement.lang = lang;
    } catch (e) {
      /* ignore */
    }
    if (viewingAs) return; // don't overwrite the Admin's own choice
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch (e) {
      /* private mode - fine, it just won't be remembered on this device */
    }
  }, [lang]);

  // On sign-in, pick up the language saved on the person's profile.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setUid(user ? user.uid : null);
      if (!user || viewingAs) return;
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const saved = snap.exists() ? snap.data()?.language : null;
        if (SUPPORTED.includes(saved)) setLangState(saved);
      } catch (e) {
        // Weak signal: keep whatever this device had.
      }
    });
    return () => unsub();
  }, []);

  const setLang = useCallback(
    (next) => {
      if (!SUPPORTED.includes(next)) return;
      setLangState(next);
      if (uid && !viewingAs) {
        updateDoc(doc(db, "users", uid), { language: next }).catch((e) =>
          console.warn("Could not save language preference:", e)
        );
      }
    },
    [uid]
  );

  const toggleLang = useCallback(() => setLang(lang === "en" ? "es" : "en"), [lang, setLang]);

  const t = useCallback((text, vars) => translateUi(lang, text, vars), [lang]);

  const value = useMemo(
    () => ({ lang, isSpanish: lang === "es", setLang, toggleLang, t }),
    [lang, setLang, toggleLang, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);

/** Locale string for toLocaleString / toLocaleDateString / react-calendar. */
export const localeFor = (lang) => (lang === "es" ? "es-US" : "en-US");
